export type DifftasticTone = "default" | "muted" | "removed" | "added" | "accent"
export type DifftasticLineKind = "context" | "removed" | "added" | "changed"

export interface DifftasticSegment {
    text: string
    tone: DifftasticTone
    bold: boolean
    dim: boolean
}

export interface DifftasticLine {
    segments: DifftasticSegment[]
    kind: DifftasticLineKind
}

interface AnsiState {
    tone: DifftasticTone
    bold: boolean
    dim: boolean
}

const SGR_SEQUENCE = new RegExp(`${String.fromCharCode(27)}\\[([0-9;]*)m`, "g")

function applySgrCode(state: AnsiState, code: number): void {
    if (code === 0) {
        state.tone = "default"
        state.bold = false
        state.dim = false
        return
    }
    if (code === 1) {
        state.bold = true
        return
    }
    if (code === 2) {
        state.dim = true
        return
    }
    if (code === 22) {
        state.bold = false
        state.dim = false
        return
    }
    if (code === 39) {
        state.tone = "default"
        return
    }
    if (code === 30 || code === 90) {
        state.tone = "muted"
        return
    }
    if (code === 31 || code === 91) {
        state.tone = "removed"
        return
    }
    if (code === 32 || code === 92) {
        state.tone = "added"
        return
    }
    if (code === 33 || code === 93 || code === 34 || code === 94 || code === 35 || code === 95 || code === 36 || code === 96) {
        state.tone = "accent"
    }
}

function lineKind(segments: DifftasticSegment[]): DifftasticLineKind {
    const removed = segments.some((segment) => segment.tone === "removed")
    const added = segments.some((segment) => segment.tone === "added")
    if (removed && added) return "changed"
    if (removed) return "removed"
    if (added) return "added"
    return "context"
}

export function parseDifftasticAnsi(output: string): DifftasticLine[] {
    const state: AnsiState = { tone: "default", bold: false, dim: false }
    return output.split("\n").map((rawLine) => {
        const segments: DifftasticSegment[] = []
        let cursor = 0
        SGR_SEQUENCE.lastIndex = 0
        for (let match = SGR_SEQUENCE.exec(rawLine); match; match = SGR_SEQUENCE.exec(rawLine)) {
            if (match.index > cursor) {
                segments.push({ text: rawLine.slice(cursor, match.index), ...state })
            }
            const codes = match[1] === "" ? [0] : match[1].split(";").map(Number)
            for (const code of codes) applySgrCode(state, code)
            cursor = match.index + match[0].length
        }
        if (cursor < rawLine.length) {
            segments.push({ text: rawLine.slice(cursor), ...state })
        }
        return { segments, kind: lineKind(segments) }
    })
}
